import React, { useState, useEffect } from 'react';
import { CannCompoundingSurveyData } from '../../types/survey.types';
import { useSurveyValidation } from '../../hooks/useSurveyValidation';
import {
  TextInput,
  Dropdown,
  RadioGroup,
  MultiSelect,
  TextArea,
  DropdownOption,
  RadioOption,
  MultiSelectOption
} from '../common/FormElements';

interface CannCompoundingSurveyProps {
  onSubmit: (data: CannCompoundingSurveyData) => void;
  loading?: boolean;
  initialData?: Partial<CannCompoundingSurveyData>;
}

const CannCompoundingSurvey: React.FC<CannCompoundingSurveyProps> = ({
  onSubmit,
  loading = false,
  initialData = {}
}) => {
  const { validateForm, getFieldError } = useSurveyValidation();

  const [formData, setFormData] = useState<Partial<CannCompoundingSurveyData>>({
    patientInitials: '',
    age: undefined,
    gender: undefined,
    productName: '',
    clinicalConditions: [],
    otherCondition: '',
    weeksOfUse: undefined,
    previousCannabisUse: undefined,
    previousProducts: '',
    satisfactionRating: undefined,
    sideEffects: '',
    additionalFeedback: '',
    ...initialData
  });

  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [canSubmit, setCanSubmit] = useState(false);
  const totalSteps = 4;

  // Auto-save to localStorage
  useEffect(() => {
    const draftKey = 'cannCompoundingSurveyDraft';
    localStorage.setItem(draftKey, JSON.stringify(formData));
  }, [formData]);

  // Load draft on mount
  useEffect(() => {
    const draftKey = 'cannCompoundingSurveyDraft';
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft && !initialData) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        setFormData(prev => ({ ...prev, ...parsedDraft }));
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [initialData]);

  // Handle submission state when step changes
  useEffect(() => {
    if (currentStep === 4) {
      setCanSubmit(false);
      // Enable submission after a short delay to allow users to review
      const timer = setTimeout(() => {
        setCanSubmit(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setCanSubmit(false);
    }
  }, [currentStep]);

  const updateField = (field: keyof CannCompoundingSurveyData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const genderOptions: DropdownOption[] = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Non-binary', label: 'Non-binary' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  const clinicalConditionOptions: MultiSelectOption[] = [
    { value: 'Chronic pain', label: 'Chronic pain' },
    { value: 'Anxiety', label: 'Anxiety' },
    { value: 'Depression', label: 'Depression' },
    { value: 'Sleep disturbance', label: 'Sleep disturbance' },
    { value: 'Insomnia', label: 'Insomnia' },
    { value: 'Sleep apnoea', label: 'Sleep apnoea' },
    { value: 'PTSD', label: 'PTSD' },
    { value: 'Epilepsy', label: 'Epilepsy' },
    { value: 'Nausea/vomiting', label: 'Nausea/vomiting' },
    { value: 'Appetite loss', label: 'Appetite loss' },
    { value: 'Other', label: 'Other (please specify)' }
  ];

  const previousCannabisOptions: RadioOption[] = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  const satisfactionOptions: RadioOption[] = [
    { value: '1', label: '1 - Very dissatisfied', description: 'The product did not meet my expectations at all' },
    { value: '2', label: '2 - Dissatisfied', description: 'The product fell short of my expectations' },
    { value: '3', label: '3 - Neither dissatisfied nor satisfied', description: 'The product met some expectations' },
    { value: '4', label: '4 - Satisfied', description: 'The product met most of my expectations' },
    { value: '5', label: '5 - Very satisfied', description: 'The product exceeded my expectations' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only allow submission on step 4 and when explicitly enabled
    if (currentStep !== 4 || !canSubmit) {
      return;
    }

    const validation = validateForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      // Scroll to first error
      const firstErrorField = document.querySelector('[aria-describedby*="error"]');
      firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setValidationErrors([]);
    onSubmit(formData as CannCompoundingSurveyData);
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepProgress = () => {
    return (currentStep / totalSteps) * 100;
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>

      <TextInput
        id="patientInitials"
        label="Patient Initials"
        value={formData.patientInitials || ''}
        onChange={(value) => updateField('patientInitials', value.toUpperCase())}
        placeholder="ABC"
        required
        maxLength={3}
        error={getFieldError(validationErrors, 'patientInitials')}
        helpText="Enter your initials (maximum 3 letters)"
      />

      <TextInput
        id="age"
        label="Age"
        type="number"
        value={formData.age?.toString() || ''}
        onChange={(value) => {
          const trimmedValue = value.trim();
          if (trimmedValue === '') {
            updateField('age', undefined);
          } else {
            const numValue = parseInt(trimmedValue);
            updateField('age', isNaN(numValue) ? undefined : numValue);
          }
        }}
        placeholder="25"
        required
        error={getFieldError(validationErrors, 'age')}
        helpText="Must be 18 years or older"
      />

      <Dropdown
        id="gender"
        label="Gender"
        value={formData.gender || ''}
        onChange={(value) => updateField('gender', value)}
        options={genderOptions}
        required
        error={getFieldError(validationErrors, 'gender')}
      />

      <TextInput
        id="productName"
        label="Product Name"
        value={formData.productName || ''}
        onChange={(value) => updateField('productName', value)}
        placeholder="Enter the cannabis product name"
        required
        error={getFieldError(validationErrors, 'productName')}
        helpText="The name of the compounded cannabis product you are reviewing"
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Clinical Information</h3>

      <MultiSelect
        id="clinicalConditions"
        label="Clinical Condition(s)"
        selectedValues={formData.clinicalConditions || []}
        onChange={(values) => updateField('clinicalConditions', values)}
        options={clinicalConditionOptions}
        required
        error={getFieldError(validationErrors, 'clinicalConditions')}
        helpText="Select all conditions for which you are using this product"
        showOtherOption={true}
        otherValue={formData.otherCondition || ''}
        onOtherChange={(value) => updateField('otherCondition', value)}
      />

      <TextInput
        id="weeksOfUse"
        label="Duration of Use (weeks)"
        type="number"
        value={formData.weeksOfUse?.toString() || ''}
        onChange={(value) => {
          const trimmedValue = value.trim();
          if (trimmedValue === '') {
            updateField('weeksOfUse', undefined);
          } else {
            const numValue = parseInt(trimmedValue);
            updateField('weeksOfUse', isNaN(numValue) ? undefined : numValue);
          }
        }}
        placeholder="4"
        required
        error={getFieldError(validationErrors, 'weeksOfUse')}
        helpText="Minimum 3 weeks required to complete this survey"
      />

      <RadioGroup
        id="previousCannabisUse"
        label="Have you used cannabis products before this prescription?"
        value={formData.previousCannabisUse?.toString() || ''}
        onChange={(value) => updateField('previousCannabisUse', value === 'true')}
        options={previousCannabisOptions}
        required
        error={getFieldError(validationErrors, 'previousCannabisUse')}
        layout="horizontal"
      />

      {formData.previousCannabisUse === true && (
        <TextArea
          id="previousProducts"
          label="Previous Cannabis Products"
          value={formData.previousProducts || ''}
          onChange={(value) => updateField('previousProducts', value)}
          placeholder="Please describe any cannabis products you have used previously"
          required
          error={getFieldError(validationErrors, 'previousProducts')}
          helpText="Include product names, types, and duration of use if known"
          rows={3}
        />
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Satisfaction & Feedback</h3>

      <RadioGroup
        id="satisfactionRating"
        label="Overall Satisfaction Rating"
        value={formData.satisfactionRating?.toString() || ''}
        onChange={(value) => updateField('satisfactionRating', parseInt(value))}
        options={satisfactionOptions}
        required
        error={getFieldError(validationErrors, 'satisfactionRating')}
        helpText="Please rate your overall satisfaction with this product"
      />

      <TextArea
        id="sideEffects"
        label="Side Effects (Optional)"
        value={formData.sideEffects || ''}
        onChange={(value) => updateField('sideEffects', value)}
        placeholder="Please describe any side effects you experienced"
        helpText="Include any adverse reactions, discomfort, or unexpected effects"
        rows={4}
        maxLength={1000}
        showCharCount={true}
      />

      <TextArea
        id="additionalFeedback"
        label="Additional Feedback (Optional)"
        value={formData.additionalFeedback || ''}
        onChange={(value) => updateField('additionalFeedback', value)}
        placeholder="Any additional comments or suggestions"
        helpText="Share any other thoughts about the product, packaging, or experience"
        rows={4}
        maxLength={1000}
        showCharCount={true}
      />
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 4: Review & Submit</h3>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-yellow-900 font-medium">
          <strong>Please review your responses carefully.</strong> Once you click "Submit Survey" below, your responses will be sent to our system and cannot be changed.
        </p>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-4">Your responses:</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div><strong>Initials:</strong> {formData.patientInitials}</div>
          <div><strong>Age:</strong> {formData.age}</div>
          <div><strong>Gender:</strong> {formData.gender}</div>
          <div><strong>Product:</strong> {formData.productName}</div>
          <div><strong>Conditions:</strong> {formData.clinicalConditions?.join(', ')}</div>
          <div><strong>Duration:</strong> {formData.weeksOfUse} weeks</div>
          <div><strong>Previous Use:</strong> {formData.previousCannabisUse ? 'Yes' : 'No'}</div>
          <div><strong>Satisfaction:</strong> {formData.satisfactionRating}/5</div>
        </div>

        {formData.sideEffects && (
          <div className="mt-4">
            <strong>Side Effects:</strong>
            <p className="text-gray-700">{formData.sideEffects}</p>
          </div>
        )}

        {formData.additionalFeedback && (
          <div className="mt-4">
            <strong>Additional Feedback:</strong>
            <p className="text-gray-700">{formData.additionalFeedback}</p>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900 font-medium">
          <strong>Privacy Notice:</strong> Your responses will be used to improve our products and services.
          No personally identifiable information is stored, and all data is encrypted and handled according
          to healthcare privacy regulations.
        </p>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Cann Compounding Cannabis Products QA Survey
        </h2>
        <p className="text-gray-600">
          Your feedback helps us improve our compounded cannabis products and services.
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Step {currentStep} of {totalSteps}</span>
          <span>{Math.round(getStepProgress())}% complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${getStepProgress()}%` }}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Error Summary */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-medium mb-2">Please correct the following errors:</h3>
            <ul className="list-disc list-inside text-sm text-red-700">
              {validationErrors.map((error, index) => (
                <li key={index}>{error.message}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Step Content */}
        <div className="min-h-[400px]">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className={`btn-outline btn-sm ${currentStep === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Previous
          </button>

          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              className="btn-primary"
            >
              Next
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading || !canSubmit}
              className={`px-8 py-3 font-bold text-lg rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl ${
                loading || !canSubmit
                  ? 'cursor-not-allowed'
                  : ''
              }`}
              style={{
                backgroundColor: loading || !canSubmit ? '#9CA3AF' : '#059669',
                color: loading || !canSubmit ? '#6B7280' : '#FFFFFF',
                border: loading || !canSubmit ? 'none' : '2px solid #047857'
              }}
              onMouseEnter={(e) => {
                if (!loading && canSubmit) {
                  e.currentTarget.style.backgroundColor = '#047857';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && canSubmit) {
                  e.currentTarget.style.backgroundColor = '#059669';
                }
              }}
            >
              {loading ? '⏳ Submitting Survey...' : !canSubmit ? '⌛ Review your responses...' : '✓ Submit Survey'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CannCompoundingSurvey;